import { Classes, Dialog, InputGroup, MenuItem, TextArea } from "@blueprintjs/core"
import { PlanrPopover } from "modules/common/components/planr/popover/Popover"
import { ItemRenderer, MultiSelect } from "@blueprintjs/select"
import { FileLink } from "modules/common/components/files/FileLink"
import { DatePicker } from "modules/common/components/form/DatePicker"
import {
  filterProjectItemPredicate,
  ProjectSelect,
  ProjectSelectedOption,
  renderProjectOption,
} from "modules/common/components/form/ProjectSelect"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { DATE_FORMAT } from "modules/common/constants"
import { isNewlyCreated } from "modules/common/models/entity"
import {
  filterItemPredicate,
  matchTargetWidthPopover,
  renderSingleOption,
  SimpleSelect,
} from "modules/common/services/form/select"
import { texts } from "modules/common/texts"
import { OrderDictionaryItemType, OrderDictionaryType } from "modules/orders-manage/models/order-dictionary"
import {
  MessageType,
  OrderQuestionSnapshotType,
  SaveQuestion,
} from "modules/orders-manage/models/order-question"
import { EmployerDictionaryType } from "modules/spending/employee/models/employee-dictionary"
import { observer } from "mobx-react"
import moment from "moment"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  BoardColumnSnapshotType,
  COLUMN_COLORS,
  DEFAULT_TAG_COLOR,
  QuestionTagSnapshotType,
  SystemColumns,
} from "../models/board"
import styles from "./QuestionDialog.module.scss"
import { QuestionComposer, QuestionFeed } from "./QuestionFeed"

const PeopleSelect = MultiSelect.ofType<SelectItem>()
const TagMultiSelect = MultiSelect.ofType<SelectItem>()

/** «Иванов И.И.» → «ИИ»: кружок исполнителя в блоке готовности */
const initials = (name: string) => {
  const parts = name.split(" ").filter((p) => !!p)
  return parts.length ? `${parts[0][0] || ""}${parts.length > 1 ? parts[1][0] : ""}`.toUpperCase() : "?"
}

/** Выпадающий список ровно по ширине поля */
const MATCH_TARGET_WIDTH = matchTargetWidthPopover()

/**
 * Пункт списка с отметкой выбранного. Общий рендерер знает только про modifiers.active —
 * это подсветка пункта под клавиатурой, из-за неё первый пункт выглядел выбранным
 */
const renderCheckedOption = (
  selected: string[],
  dot?: (id: string) => string
): ItemRenderer<SelectItem> => (item, { handleClick, modifiers }) => {
  if (!modifiers.matchesPredicate) {
    return null
  }

  const checked = selected.includes(item.id)

  return (
    <MenuItem
      key={item.id}
      // подсветку пункта под клавиатурой делаем серой: синий фон читался как «выбрано»
      className={modifiers.active ? styles.optionActive : ""}
      disabled={modifiers.disabled}
      icon={checked ? "tick" : "blank"}
      onClick={handleClick}
      shouldDismissPopover={false}
      text={
        dot ? (
          <span className={styles.optionWithDot}>
            <span className={styles.tagDot} style={{ backgroundColor: dot(item.id) }} />
            <span style={{ color: dot(item.id) }}>{item.label}</span>
          </span>
        ) : (
          item.label
        )
      }
    />
  )
}

/** Окно задания: заголовок, описание, ответ, комментарии и боковая панель свойств */
export const QuestionDialog: React.FC<QuestionDialogProps> = observer((props) => {
  const {
    value,
    question,
    columns,
    tags,
    employee,
    orders,
    baseUrl,
    hideProject,
    currentUserId,
    canAddQuestion,
    onClose,
    onSubmit,
    onRemove,
    onAddMessage,
    onDeleteMessage,
    onCreateChild,
    onOpenQuestion,
    onSetReadiness,
    onShare,
    portfolioMembers,
    upload,
    inline,
    onDirtyChange,
    boardName,
  } = props

  const [model, setModel] = useState<SaveQuestion>(value)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  // подсвечиваем пустое задание только после попытки сохранить, а не сразу при открытии
  const [emptyError, setEmptyError] = useState(false)

  useEffect(() => {
    setModel(value)
    setDirty(false)
    setEmptyError(false)
  }, [value])

  // список заданий спрашивает перед переходом к другой строке, есть ли несохранённое
  useEffect(() => {
    onDirtyChange && onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  const isNew = isNewlyCreated(model.id)
  const canEdit = isNew ? !!canAddQuestion : question.canEditQuestion
  const canAssign = isNew ? !!canAddQuestion : question.canAddImplementer || question.canEditQuestion

  const patch = useCallback((changes: Partial<SaveQuestion>) => {
    setModel((prev) => ({ ...prev, ...changes }))
    setDirty(true)

    if ("title" in changes || "description" in changes) {
      setEmptyError(false)
    }
  }, [])

  const close = useCallback(() => {
    if (dirty && !window.confirm("У вас есть несохранённые изменения. Закрыть окно?")) {
      return
    }

    onClose()
  }, [dirty, onClose])

  const submit = useCallback(async () => {
    // задание без заголовка и без описания сохранять нельзя — хватает одного из двух,
    // быстрое добавление на доске, например, создаёт карточку только с заголовком
    if (!model.title?.trim() && !model.description?.trim()) {
      setEmptyError(true)
      return
    }

    setSaving(true)
    const success = await onSubmit(model)
    setSaving(false)

    if (success) {
      setDirty(false)
    }
  }, [model, onSubmit])

  // «Просрочено» заполняется по сроку, вручную в неё не ставят
  const movable = useMemo(
    () =>
      columns.filter(
        (c) => c.key !== SystemColumns.expired && (c.key !== SystemColumns.done || question.canCompleteQuestion || isNew)
      ),
    [columns, question.canCompleteQuestion, isNew]
  )

  const currentColumn = columns.find((c) => c.key === (model.columnId || SystemColumns.inWork))

  // подсказка про перенос имеет смысл только на доске с системными колонками
  const reviewColumn = columns.find((c) => c.key === SystemColumns.review)


  const columnItems = useMemo(() => movable.map((c) => ({ id: c.key, label: c.name })), [movable])

  const columnItem = columnItems.find((c) => c.id === model.columnId) || null

  // у заданий без срока с сервера может прийти пустая дата (01.01.0001) — её не показываем
  const stopDate = useMemo(() => {
    const parsed = model.stopDate ? moment(model.stopDate, DATE_FORMAT) : null

    return parsed && parsed.isValid() && parsed.year() > 1900 ? parsed : null
  }, [model.stopDate])

  // в архивной колонке срок не подсвечиваем: задание туда уже сложили
  const expired =
    !model.completed && !currentColumn?.isArchive && !!stopDate && stopDate.isBefore(moment().startOf("day"))

  const project = orders && model.orderId ? orders.orders.find((o) => o.id === model.orderId) || null : null

  // работу закрывают исполнители, а если их не назначили — ответственные
  const executors = useMemo(
    () => (question.implementers.length > 0 ? question.implementers : question.employers),
    [question.implementers, question.employers]
  )

  const readiness = question.readiness
  const readyCount = readiness.filter((r) => executors.some((e) => e.id === r.employer?.id)).length
  const iAmExecutor = executors.some((e) => e.id === currentUserId)
  const myReadiness = readiness.some((r) => r.employer?.id === currentUserId)

  // на личной доске задания раздают только между её участниками
  const people = useMemo(() => {
    if (!portfolioMembers) {
      return employee.employee
    }

    return portfolioMembers.map((member) => ({ id: member.id, label: member.name }))
  }, [portfolioMembers, employee.employee])

  // моя отметка — последняя: после неё задание уйдёт на проверку само
  const iAmLast = iAmExecutor && !myReadiness && readyCount === executors.length - 1

  // задание самому себе: отмечаться не перед кем, достаточно перенести карточку
  const selfAssigned =
    model.selfQuestion ||
    (executors.length === 1 && !!question.author && executors[0].id === question.author.id)

  const showReadiness = !isNew && !selfAssigned && executors.length > 0

  // ответственный, автор задания, ГИП и сотрудник с полным доступом отмечают за исполнителей
  const canManageReadiness = question.canManageReadiness

  // подсказка про проверку — исполнителю, который ещё не сдал работу, на доске с системными колонками
  const showReviewHint =
    !!reviewColumn &&
    iAmExecutor &&
    !myReadiness &&
    model.columnId !== SystemColumns.review &&
    model.columnId !== SystemColumns.done

  const [readinessSaving, setReadinessSaving] = useState(false)

  const onReadiness = useCallback(
    async (ready: boolean, employerId?: string) => {
      setReadinessSaving(true)
      await onSetReadiness(model.id, ready, employerId)
      setReadinessSaving(false)
    },
    [model.id, onSetReadiness]
  )

  // лента сохранённого задания — с сервера: комментарии пишутся сразу, без «Сохранить»
  const messages = (isNew ? model.messages : question.messages) as MessageType[]

  const addMessage = useCallback(
    async (message: MessageType, files: FileBase[]) => {
      if (!isNew) {
        return onAddMessage(
          model.id,
          message,
          files.map((f) => f.fileId)
        )
      }

      patch({ messages: [message, ...model.messages] })
      return true
    },
    [isNew, model.id, model.messages, onAddMessage, patch]
  )

  const removeMessage = useCallback(
    (message: MessageType, index: number) => {
      if (!isNew && message.id && onDeleteMessage) {
        onDeleteMessage(model.id, message.id)
        return
      }

      patch({ messages: model.messages.filter((_, i) => i !== index) })
    },
    [isNew, model.id, model.messages, onDeleteMessage, patch]
  )

  const editMessage = useCallback(
    (message: MessageType) =>
      isNew
        ? Promise.resolve(true)
        : onAddMessage(
            model.id,
            message,
            message.files.map((f) => f.id)
          ),
    [isNew, model.id, onAddMessage]
  )

  const overdueDays = expired && stopDate ? moment().startOf("day").diff(stopDate.clone().startOf("day"), "days") : 0
  const projectLabel = project ? project.label : question.order && question.order.name.trim() ? question.order.name : ""

  /** Шапка: где лежит задание, заголовок и главное о нём — колонка, срок, готовность */
  const head = (
    <div className={styles.head}>
      <div className={styles.headTop}>
        <span className={styles.crumbs}>
          {boardName && <span>{boardName}</span>}
          {projectLabel && <span>{projectLabel}</span>}
          <b>{isNew ? "Новое задание" : question.number}</b>
        </span>

        <span className={styles.spacer} />

        {dirty && !isNew && <span className={styles.unsaved}>Есть несохранённые изменения</span>}

        {/* ссылкой на задание делятся прямо из карточки: адрес у него постоянный */}
        {!isNew && onShare && (
          <PlanrButton
            type="graybtn"
            icon="general-copy"
            size="small"
            round={true}
            title="Скопировать ссылку на задание"
            onClick={() => onShare(model.id)}
          />
        )}
        <PlanrButton
          type="graybtn"
          icon="general-cross-small"
          size="small"
          round={true}
          title="Закрыть"
          onClick={close}
        />
      </div>

      <InputGroup
        className={`${styles.titleInput} ${emptyError ? styles.titleError : ""}`}
        value={model.title}
        placeholder="Коротко о задании"
        readOnly={!canEdit}
        autoComplete="off"
        aria-label="Заголовок задания"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ title: e.target.value })}
      />

      <div className={styles.chips}>
        <SimpleSelect
          activeItem={columnItem}
          items={columnItems}
          itemRenderer={renderSingleOption}
          filterable={false}
          disabled={!isNew && !question.canMoveQuestion}
          onItemSelect={(item: SelectItem) => patch({ columnId: item.id, completed: item.id === SystemColumns.done })}
          popoverProps={{ rootBoundary: "viewport", minimal: true }}
        >
          <button
            type="button"
            className={styles.columnPill}
            style={{ backgroundColor: currentColumn ? currentColumn.color : undefined }}
            disabled={!isNew && !question.canMoveQuestion}
            title="Колонка доски"
          >
            {currentColumn ? currentColumn.name : "Колонка"}
            <GeneralIcon type="general-chevron-down" size={12} />
          </button>
        </SimpleSelect>

        <span className={`${styles.chip} ${expired ? styles.chipDanger : ""}`}>
          <GeneralIcon type="general-calendar" size={12} />
          {stopDate
            ? expired
              ? `Срок ${stopDate.format("DD.MM")} · просрочено на ${overdueDays} дн.`
              : `Срок ${stopDate.format(DATE_FORMAT)}`
            : "Без срока"}
        </span>

        {showReadiness && (
          <span className={`${styles.chip} ${readyCount === executors.length ? styles.chipOk : ""}`}>
            Готовность {readyCount} из {executors.length}
          </span>
        )}
      </div>
    </div>
  )

  /** Исполнители и их готовность: свою отмечает каждый, за других — кто распоряжается заданием */
  const readinessBlock = showReadiness && (
    <div className={styles.readiness}>
      <div className={styles.readinessHead}>
        <span>Исполнители</span>
        <span className={styles.readinessCount}>
          {readyCount} из {executors.length} выполнили
        </span>
      </div>
      <div className={styles.progress}>
        <div style={{ width: `${executors.length ? (readyCount / executors.length) * 100 : 0}%` }} />
      </div>

      {executors.map((person) => {
        const ready = readiness.find((r) => r.employer?.id === person.id)
        const mine = person.id === currentUserId
        const editable = mine || canManageReadiness

        return (
          <button
            type="button"
            key={person.id}
            className={`${styles.readinessRow} ${editable ? styles.readinessEditable : ""} ${ready ? styles.readinessDone : ""}`}
            disabled={!editable || readinessSaving}
            title={
              !editable
                ? undefined
                : mine
                ? ready
                  ? "Снять свою отметку"
                  : "Отметить выполненным"
                : ready
                ? `Снять отметку за ${person.name}`
                : `Отметить выполненным за ${person.name}`
            }
            onClick={() => editable && onReadiness(!ready, mine ? undefined : person.id)}
          >
            <span className={styles.readinessAvatar}>{initials(person.name)}</span>
            <span className={styles.readinessName}>{person.name}</span>
            <span className={styles.readinessState}>
              {ready ? `выполнено ${ready.day}` : editable && !mine ? "отметить за него" : "в работе"}
            </span>
          </button>
        )
      })}

      {iAmExecutor && !myReadiness && (
        <PlanrButton
          type="greenish"
          size="small"
          disabled={readinessSaving}
          onClick={() => onReadiness(true)}
          title={iAmLast ? "Вы последний — после отметки задание уйдёт на проверку" : undefined}
        >
          Отметить выполненным
        </PlanrButton>
      )}

      {showReviewHint && (
        <div className={styles.reviewHint}>
          <GeneralIcon type="general-info" size={14} />
          <span>
            {iAmLast
              ? "Вы последний — как только отметитесь, задание уйдёт на проверку"
              : "Результат можно отправить комментарием — отметьте «Это результат»"}
          </span>
        </div>
      )}

      {question.reviewSentBy && (
        <div className={styles.reviewSent}>
          На проверку отправил {question.reviewSentBy.name}
          {question.reviewSentDate ? `, ${question.reviewSentDate}` : ""}
        </div>
      )}
    </div>
  )

  /** Свойства задания: кто, к какому проекту, срок, метки */
  const properties = (
    <div className={styles.props}>
      {isNew && canAddQuestion && (
        <Field label="Кому">
          <div className={styles.switcher}>
            <PlanrButton type={!model.selfQuestion ? "primary" : "graybtn"} size="small" onClick={() => patch({ selfQuestion: false })}>
              Сотруднику
            </PlanrButton>
            <PlanrButton
              type={model.selfQuestion ? "primary" : "graybtn"}
              size="small"
              onClick={() => patch({ selfQuestion: true, employerIds: [], implementerIds: [] })}
            >
              Себе
            </PlanrButton>
          </div>
        </Field>
      )}

      <Field label="Срок">
        <div className={expired ? styles.expiredDate : undefined}>
          <DatePicker
            rootBoundary="viewport"
            value={stopDate ? stopDate.toDate() : null}
            clasName="planr-default-input"
            // срок задания меняет тот, кто его выдал, а не исполнители
            disabled={!canEdit}
            onChange={(date) => patch({ stopDate: date ? moment(date).format(DATE_FORMAT) : "" })}
          />
        </div>
        {question.oldStopDates && question.oldStopDates.length > 0 && (
          <div className={styles.oldDates}>Прежние сроки: {question.oldStopDates.join(", ")}</div>
        )}
      </Field>

      {orders && !hideProject && (
        <Field label="Проект">
          <ProjectSelect
            className="full-width-select"
            filterable={true}
            itemPredicate={filterProjectItemPredicate}
            activeItem={project}
            items={orders.orders}
            itemRenderer={renderProjectOption}
            disabled={!canEdit}
            onItemSelect={(item: OrderDictionaryItemType | null) =>
              patch({
                orderId: item ? item.id : "",
                // ГИП проекта подставляется ответственным, только если он вообще может
                // получить задание на этой доске — иначе бэк его отбросит и поле опустеет
                employerIds:
                  item &&
                  item.employer &&
                  model.employerIds.length === 0 &&
                  people.some((person) => person.id === item.employer!.id)
                    ? [item.employer.id]
                    : model.employerIds,
              })
            }
            resetOnClose={true}
            inputProps={{ placeholder: "Проект" }}
          >
            <ProjectSelectedOption option={project} onClear={() => patch({ orderId: "" })} />
          </ProjectSelect>
        </Field>
      )}

      <Field label="Ответственные">
        <People
          people={people}
          selected={model.employerIds}
          assigned={question.employers}
          disabled={!canAssign || model.selfQuestion}
          onChange={(ids) => patch({ employerIds: ids })}
        />
      </Field>

      <Field label="Исполнители">
        <People
          people={people}
          selected={model.implementerIds}
          assigned={question.implementers}
          disabled={!canAssign || model.selfQuestion}
          onChange={(ids) => patch({ implementerIds: ids })}
        />
      </Field>

      <Field label="Метки">
        <TagsSelect
          known={tags}
          selected={model.tags}
          colors={model.tagColors}
          disabled={!canEdit}
          onChange={(value) => patch({ tags: value })}
          onColor={(tag, color) => patch({ tagColors: { ...model.tagColors, [tag]: color } })}
        />
      </Field>

      {!isNew && (
        <div className={styles.info}>
          Создал {question.author?.name || "—"} · {question.day}
        </div>
      )}
    </div>
  )

  // файлы описания: кнопкой или перетаскиванием в блок, как в поле комментария
  const [descDragOver, setDescDragOver] = useState(false)
  const descPicker = useRef<HTMLInputElement>(null)

  const attachToDescription = useCallback(
    async (list: FileList | null) => {
      if (!list) {
        return
      }

      const added: FileBase[] = []
      for (const file of Array.from(list)) {
        const uploaded = await upload(file)
        uploaded && added.push(uploaded)
      }

      added.length > 0 && setModel((prev) => ({ ...prev, questionFiles: [...prev.questionFiles, ...added] }))
      added.length > 0 && setDirty(true)
    },
    [upload]
  )

  const description = (
    <div className={styles.block}>
      <h3 className={styles.blockTitle}>Описание</h3>

      {/* тот же вид, что у поля комментария: текст, файлы и кнопка «Файл» в одной рамке */}
      <div
        className={`${styles.descBox} ${descDragOver ? styles.descDragOver : ""} ${emptyError ? styles.descError : ""}`}
        onDragOver={(e) => {
          if (!canEdit) {
            return
          }

          e.preventDefault()
          setDescDragOver(true)
        }}
        onDragLeave={() => setDescDragOver(false)}
        onDrop={(e) => {
          if (!canEdit) {
            return
          }

          e.preventDefault()
          setDescDragOver(false)
          attachToDescription(e.dataTransfer.files)
        }}
      >
        <TextArea
          className={`${Classes.FILL} ${styles.descInput}`}
          value={model.description}
          autoResize={true}
          readOnly={!canEdit}
          aria-label="Описание задания"
          placeholder={canEdit ? "Что нужно сделать… файлы можно перетащить сюда" : "Описания нет"}
          onChange={(e) => patch({ description: e.target.value })}
        />

        {canEdit && (
          <div className={styles.descBar}>
            <input
              ref={descPicker}
              type="file"
              multiple={true}
              hidden={true}
              onChange={(e) => {
                attachToDescription(e.target.files)
                e.target.value = ""
              }}
            />
            <button
              type="button"
              className={styles.attach}
              title="Прикрепить файлы к заданию"
              onClick={() => descPicker.current && descPicker.current.click()}
            >
              <GeneralIcon type="general-attach" size={14} />
              Файл
            </button>
          </div>
        )}
      </div>

      {emptyError && <div className={styles.emptyError}>Заполните заголовок или описание задания</div>}

      {/* прикреплённые файлы — под полем, как вложения письма */}
      {model.questionFiles.length > 0 && (
        <div className={styles.files}>
          {model.questionFiles.map((doc, index) => (
            <FileLink
              baseUrl={baseUrl}
              file={doc}
              key={doc.fileId || index}
              readOnly={!canEdit}
              onRemove={(file: FileBase) =>
                patch({ questionFiles: model.questionFiles.filter((f) => f.fileId !== file.fileId) })
              }
            />
          ))}
        </div>
      )}
    </div>
  )

  const childrenDone = question.children.filter((c) => c.completed).length

  const subtasks = !isNew && (question.children.length > 0 || question.canEditQuestion) && (
    <div className={styles.block}>
      <div className={styles.blockHead}>
        <h3 className={styles.blockTitle}>Подзадачи</h3>
        {question.children.length > 0 && (
          <>
            <span className={styles.blockCount}>
              {childrenDone} из {question.children.length}
            </span>
            <div className={`${styles.progress} ${styles.progressShort}`}>
              <div style={{ width: `${(childrenDone / question.children.length) * 100}%` }} />
            </div>
          </>
        )}
        <span className={styles.spacer} />
        {question.canEditQuestion && (
          <PlanrButton type="dashed" size="small" icon="general-plus-small" onClick={() => onCreateChild(question)}>
            Подзадача
          </PlanrButton>
        )}
      </div>

      {question.children.length > 0 && (
        <div className={styles.children}>
          {question.children.map((child) => (
            <button
              type="button"
              className={`${styles.child} ${child.completed ? styles.childDone : ""}`}
              key={child.id}
              title={child.title || child.description}
              // только id: узел подзадачи после загрузки другого задания удаляется
              onClick={() => onOpenQuestion({ id: child.id })}
            >
              <span className={`${styles.childMark} ${child.completed ? styles.childMarkDone : ""}`} />
              <span className={styles.childNumber}>{child.number}</span>
              <span className={styles.childText}>{child.title || child.description}</span>
              <span className={styles.childTerm}>{child.stopDay}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )

  const parent = question.parentQuestion && (
    <div className={styles.block}>
      <h3 className={styles.blockTitle}>Относится к заданию</h3>
      <button
        type="button"
        className={styles.child}
        title={question.parentQuestion.title || question.parentQuestion.description}
        onClick={() => question.parentQuestion && onOpenQuestion({ id: question.parentQuestion.id })}
      >
        <span className={styles.childNumber}>{question.parentQuestion.number}</span>
        <span className={styles.childText}>{question.parentQuestion.title || question.parentQuestion.description}</span>
        <span className={styles.childTerm}>{question.parentQuestion.stopDay}</span>
      </button>
    </div>
  )

  const content = (
    <form
      className={styles.card}
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      {head}

      <div className={styles.body}>
        <div className={styles.main}>
          {description}
          {subtasks}
          {parent}

          <QuestionFeed
            messages={messages}
            currentUserId={currentUserId}
            baseUrl={baseUrl}
            onEdit={editMessage}
            onRemove={removeMessage}
          />

          {/* поле комментария прижато к низу: писать можно, не прокручивая ленту */}
          <div className={styles.composer}>
            <QuestionComposer
              canAttach={!isNew}
              canResult={!isNew && iAmExecutor && !myReadiness}
              baseUrl={baseUrl}
              upload={upload}
              onSend={addMessage}
            />
          </div>
        </div>

        <div className={styles.side}>
          {readinessBlock}
          {properties}

          {/* удаляет тот, у кого есть право: у нового задания удалять нечего */}
          {!isNew && question.canRemoveQuestion && (
            <RemoveConfirmation<SaveQuestion>
              what={() => `задание ${model.title || question.number}`}
              onConfirmed={(value) => value && onRemove(value)}
            >
              {({ confirmRemoving }) => (
                <button type="button" className={styles.remove} onClick={() => confirmRemoving(model)}>
                  Удалить задание
                </button>
              )}
            </RemoveConfirmation>
          )}
        </div>
      </div>

      {/* сохранять нужно только правки полей: комментарии и готовность уходят сразу */}
      {(dirty || isNew) && (
        <div className={styles.footer}>
          <span className={styles.footerHint}>{isNew ? "Новое задание" : "Есть несохранённые изменения"}</span>
          <PlanrButton type="graybtn" size="small" onClick={close} disabled={saving}>
            {isNew ? "Отмена" : "Отменить"}
          </PlanrButton>
          {/* как в остальных формах приложения (StandardFormButtons): сохранение — зелёной кнопкой */}
          <PlanrButton type="greenish" size="small" onClick={submit} disabled={saving}>
            {isNew ? "Создать" : texts.save}
          </PlanrButton>
        </div>
      )}
    </form>
  )

  // в списке заданий карточка стоит панелью справа от строк, а не окном поверх
  if (inline) {
    return <div className={`${styles.inline}`}>{content}</div>
  }

  return (
    <Dialog
      isOpen={true}
      onClose={close}
      backdropClassName="standard"
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
      enforceFocus={false}
      className={`figma-dialog ${styles.dialog}`}
    >
      {content}
    </Dialog>
  )
})

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className={`bp6-form-group ${Classes.SMALL} ${styles.field}`}>
    <label className="bp6-label">{label}</label>
    {children}
  </div>
)

/**
 * Метки задания: выбираются из справочника открытой доски, недостающая заводится прямо
 * здесь — дальше она предлагается в остальных заданиях этой же доски
 */
const TagsSelect: React.FC<TagsSelectProps> = ({ known, selected, colors, disabled, onChange, onColor }) => {
  // только что заведённый тег в справочник ещё не попал — показываем его в списке сразу
  const names = known.map((tag) => tag.name)
  const items = names
    .concat(selected.filter((tag) => !names.includes(tag)))
    .map((name) => ({ id: name, label: name }))

  const colorOf = (tag: string) =>
    colors[tag] || known.find((t) => t.name === tag)?.color || DEFAULT_TAG_COLOR

  const toggle = (tag: string) =>
    onChange(selected.includes(tag) ? selected.filter((t) => t !== tag) : [...selected, tag])

  return (
    <>
      <TagMultiSelect
        className="full-width-select"
        fill={true}
        itemRenderer={renderCheckedOption(selected, colorOf)}
        itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
        selectedItems={selected.map((tag) => ({ id: tag, label: tag }))}
        items={items}
        onItemSelect={(item: SelectItem) => toggle(item.id)}
        placeholder="Выберите тег или введите новый"
        itemPredicate={filterItemPredicate}
        tagRenderer={(item: SelectItem) => item.label}
        noResults={<MenuItem disabled={true} text="Начните вводить название" />}
        popoverProps={MATCH_TARGET_WIDTH}
        // список остаётся открытым, поэтому активный пункт не сбрасываем:
        // иначе Blueprint возвращает его на первый и прокручивает список к началу
        resetOnSelect={false}
        createNewItemFromQuery={(query: string) => ({ id: query.trim(), label: query.trim() })}
        createNewItemRenderer={(query: string, active: boolean, handleClick: any) => (
          <MenuItem
            icon="add"
            text={`Создать тег «${query.trim()}»`}
            active={active}
            onClick={handleClick}
            shouldDismissPopover={false}
          />
        )}
        tagInputProps={{
          disabled,
          // подсказка в поле остаётся видной и когда теги уже выбраны
          inputProps: { placeholder: "Введите название нового тега" },
          onRemove: (_value: any, index: number) => onChange(selected.filter((_, i) => i !== index)),
        }}
      />

      {selected.length > 0 && (
        <div className={styles.tagColors}>
          {selected.map((tag) => (
            <PlanrPopover
              key={tag}
              disabled={disabled}
              placement="bottom-start"
              rootBoundary="viewport"
              content={
                <div className={styles.palette}>
                  {[DEFAULT_TAG_COLOR, ...COLUMN_COLORS].map((color) => (
                    <button
                      type="button"
                      key={color}
                      className={`${styles.paletteColor} ${colorOf(tag) === color ? styles.paletteActive : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => onColor(tag, color)}
                    />
                  ))}
                </div>
              }
            >
              <button
                type="button"
                className={styles.tagChip}
                style={{ color: colorOf(tag) }}
                title="Выбрать цвет тега"
              >
                <span className={styles.tagDot} style={{ backgroundColor: colorOf(tag) }} />
                {tag}
              </button>
            </PlanrPopover>
          ))}
        </div>
      )}
    </>
  )
}

interface TagsSelectProps {
  known: QuestionTagSnapshotType[]
  selected: string[]
  colors: TStringMap<string>
  disabled?: boolean
  onChange: (tags: string[]) => void
  onColor: (tag: string, color: string) => void
}

const People: React.FC<PeopleProps> = ({ people, selected, assigned, disabled, onChange }) => {
  // уволенных в справочнике нет, но в задании они назначены — берём имя из самого задания
  const items = selected.map((id) => {
    const known = people.find((e) => e.id === id)

    if (known) {
      return { id: known.id, label: known.label }
    }

    const person = (assigned || []).find((e) => e.id === id)

    return { id, label: person ? person.name : "Сотрудник удалён" }
  })

  return (
    <PeopleSelect
      className="full-width-select"
      fill={true}
      itemRenderer={renderCheckedOption(selected)}
      itemsEqual={(a: SelectItem, b: SelectItem) => a.id === b.id}
      selectedItems={items}
      items={people}
      onItemSelect={(item: SelectItem) =>
        onChange(selected.includes(item.id) ? selected.filter((id) => id !== item.id) : [...selected, item.id])
      }
      placeholder="Не назначены"
      itemPredicate={filterItemPredicate}
      tagRenderer={(item: SelectItem) => item.label}
      noResults={<MenuItem disabled={true} text={texts.noData} />}
      popoverProps={MATCH_TARGET_WIDTH}
      // после выбора строку поиска сбрасываем: следующего ищут с чистого листа
      resetOnSelect={true}
      tagInputProps={{
        disabled,
        onRemove: (_value: any, index: number) => onChange(selected.filter((_, i) => i !== index)),
      }}
    />
  )
}

interface PeopleProps {
  people: SelectItem[]
  selected: string[]
  /** Кто уже назначен на задание: у уволенных имя есть только здесь */
  assigned?: { id: string; name: string }[]
  disabled?: boolean
  onChange: (ids: string[]) => void
}

interface QuestionDialogProps {
  value: SaveQuestion
  question: OrderQuestionSnapshotType
  columns: BoardColumnSnapshotType[]
  /** Справочник меток для подсказок */
  tags: QuestionTagSnapshotType[]
  employee: EmployerDictionaryType
  orders?: OrderDictionaryType
  baseUrl: string
  hideProject?: boolean
  currentUserId: string
  canAddQuestion?: boolean
  onClose: () => void
  /** Копирование ссылки на задание в буфер: адрес у задания постоянный */
  onShare?: (id: string) => void
  /** Участники личной доски: владелец и приглашённые. Пусто — доска по проектам */
  portfolioMembers?: { id: string; name: string }[] | null
  onSetReadiness: (questionId: string, ready: boolean, employerId?: string) => Promise<any>
  onSubmit: (value: SaveQuestion) => Promise<boolean>
  onRemove: (value: SaveQuestion) => void
  /** Комментарий; fileIds — загруженные файлы, которые станут его вложениями */
  onAddMessage: (questionId: string, message: MessageType, fileIds?: string[]) => Promise<any>
  onDeleteMessage?: (questionId: string, messageId: string) => Promise<any>
  onCreateChild: (parent: OrderQuestionSnapshotType) => void
  onOpenQuestion: (question: any) => void
  upload: (file: File) => Promise<FileBase | null>
  /** Панелью внутри страницы, а не окном: так карточку показывает список заданий */
  inline?: boolean
  /** Есть ли несохранённые изменения: список спрашивает перед переходом к другому заданию */
  onDirtyChange?: (dirty: boolean) => void
  /** Доска задания: показывается в шапке рядом с проектом и номером */
  boardName?: string
}
